import { isDate, isNumber, isString } from '../helpers';

import { CalendarType, DateFormatter, DateTransformInput, DateType } from './date-format-types';

// Constants taken from: https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
const ISO8601_DATE_REGEX =
  /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
const DATE_FORMATS_SPLIT =
  /((?:[^GyMLwWdEabBhHmsSzZO']+)|(?:'(?:[^']|'')*')|(?:G{1,5}|y{1,4}|M{1,5}|L{1,5}|w{1,2}|W{1}|d{1,2}|E{1,6}|a{1,5}|b{1,5}|B{1,5}|h{1,2}|H{1,2}|m{1,2}|s{1,2}|S{1,3}|z{1,4}|Z{1,5}|O{1,4}))([\s\S]*)/; //NOSONAR

const DATE_FORMATS: { [format: string]: DateFormatter } = {};
const JANUARY = 0;
const THURSDAY = 4;
const BUDDHIST_YEAR_OFFSET = 543;

export function getDateFromNumericFormat(dateStr: string, dateFormat: string): Date {
  if (dateStr && dateFormat) {
    dateFormat = dateFormat.toLowerCase();
    const yearPlaceholder = 'yyyy';
    const monthPlaceholder = dateFormat.includes('mm') ? 'mm' : 'm';
    const datePlaceholder = dateFormat.includes('dd') ? 'dd' : 'd';

    // split the date-format string at any char different from [ymd]-placeholders
    const formatParts = dateFormat.split(/[^ymd]+/);
    const dateSeparators = dateFormat.match(/[^ymd]+/g);
    const dRegex = new RegExp(`[${dateSeparators.join('')}]+`);

    const dateParts = dateStr.split(dRegex);

    const y = parseInt(dateParts[formatParts.indexOf(yearPlaceholder)], 10);
    const m = parseInt(dateParts[formatParts.indexOf(monthPlaceholder)], 10) - 1;
    const d = parseInt(dateParts[formatParts.indexOf(datePlaceholder)], 10);
    return new Date(y, m, d);
  }

  return new Date();
}

export function getCalendarTypeByLocale(locale: string): CalendarType {
  return new Intl.DateTimeFormat(locale).resolvedOptions().calendar as CalendarType;
}

function addYearsToDate(orgDate: Date, years: number): Date {
  const date = new Date(orgDate);
  date.setFullYear(orgDate.getFullYear() + years);

  return date;
}

/**
 * For the time being calendar-based date conversion is limited to `buddhist` only
 * as available MOs don't use any additional calendar system.
 */
export function convertToLocaleDate(orgDate: Date, locale: string): Date {
  switch (getCalendarTypeByLocale(locale)) {
    case CalendarType.BUDDHIST:
    case CalendarType.THAI:
      return addYearsToDate(orgDate, BUDDHIST_YEAR_OFFSET);
    default:
      return orgDate;
  }
}

export function convertFromLocaleDate(orgDate: Date, locale: string): Date {
  switch (getCalendarTypeByLocale(locale)) {
    case CalendarType.BUDDHIST:
    case CalendarType.THAI:
      return addYearsToDate(orgDate, -BUDDHIST_YEAR_OFFSET);
    default:
      return orgDate;
  }
}

/**
 * converts timezone to a format compatible with "timezoneToOffset" function
 * conversion examples:
 * - "Europe/Kiev" to "GMT+2"(standard time) or "GMT+3" (summer-time)
 * - "America/Costa_Rica" to "CST"
 * - "CST6CDT" to "CST"(standard time) or "CDT" (summer-time)
 */
export function convertTimezoneName(date: DateTransformInput, tz?: string): string {
  if (tz === 'GMT' || tz?.match(/^(UTC|GMT)?[\+|\-]\d{2}:?\d{2}$/)) {
    return tz;
  }
  return new Date(date).toLocaleString('en', { timeZone: tz, timeZoneName: 'short' }).split(' ').slice(-1)[0];
}

/**
 * Taken from https://github.com/angular/angular/blob/master/packages/common/src/i18n/format_date.ts
 * and altered.
 */
function timezoneToOffset(timezone: string, fallback: number): number {
  // Support: IE 11 only, Edge 13-15+
  // IE/Edge do not "understand" colon (`:`) in timezone
  timezone = timezone.replace(/:/g, '');
  const requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
  return isNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
}

function addDateMinutes(date: Date, minutes: number): Date {
  date = new Date(date.getTime());
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}

export function convertTimezoneToLocal(date: Date, timezone: string, reverse: boolean, zuluTime = false): Date {
  const reverseValue = reverse ? -1 : 1;
  const dateTimezoneOffset = zuluTime ? 0 : date.getTimezoneOffset();
  const timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
  return addDateMinutes(date, reverseValue * (timezoneOffset - dateTimezoneOffset));
}

/**
 * Taken from https://github.com/angular/angular/blob/master/packages/common/src/i18n/format_date.ts
 *
 * Difference to DatePipe:
 * - Removed getting named formats and applying them to format variable.
 * - extracted to separate method
 */
export function splitFormatPattern(format: string): string[] {
  let formatParts: string[] = [];
  let match;
  while (format) {
    match = DATE_FORMATS_SPLIT.exec(format);
    if (match) {
      formatParts = formatParts.concat(match.slice(1));
      const part = formatParts.pop();
      if (!part) {
        break;
      }
      format = part;
    } else {
      formatParts.push(format);
      break;
    }
  }
  return formatParts;
}

export function formatDatePart(formatPart: string, date: Date): string {
  const dateFormatter = getDateFormatter(formatPart);
  if (dateFormatter) {
    return dateFormatter(date /* , locale, dateTimezoneOffset */);
  }
  if (formatPart === "''") {
    return "'";
  }
  return formatPart.replace(/(^'|'$)/g, '').replace(/''/g, "'");
}

export function appendTimezoneToValue(value: DateTransformInput, timezone: string): string {
  value = value instanceof Date ? toDate(value, false).toISOString() : value;
  return `${value} ${convertTimezoneName(value, timezone)}`;
}

export function formatTimePart(date: Date, locale: string, timezoneSuffix: string, toLocaleDate: boolean): string {
  const formattedTime = date.toLocaleTimeString(locale, { timeStyle: 'short' });
  const tz = toLocaleDate ? undefined : timezoneSuffix;
  return formattedTime + (timezoneSuffix ? ' ' + convertTimezoneName(date, tz) : '');
}

// Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
function padNumber(num: number, digits: number, minusSign = '-', trim = false, negWrap = false): string {
  let neg = '';
  if (num < 0 || (negWrap && num <= 0)) {
    if (negWrap) {
      num = -num + 1;
    } else {
      num = -num;
      neg = minusSign;
    }
  }
  let strNum = String(num);
  while (strNum.length < digits) {
    strNum = '0' + strNum;
  }
  if (trim) {
    strNum = strNum.substring(strNum.length - digits);
  }
  return neg + strNum;
}

// Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
function getFirstThursdayOfYear(year: number): Date {
  const firstDayOfYear = new Date(year, JANUARY, 1).getDay();
  return new Date(year, 0, 1 + (firstDayOfYear <= THURSDAY ? THURSDAY : THURSDAY + 7) - firstDayOfYear);
}

// Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
function getThursdayThisWeek(datetime: Date): Date {
  return new Date(datetime.getFullYear(), datetime.getMonth(), datetime.getDate() + (THURSDAY - datetime.getDay()));
}

/**
 * Converts a date in ISO8601 to a Date.
 * Used instead of `Date.parse` because of browser discrepancies.
 * Added option to ignore / include timezone offset
 *
 * Taken from: https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 */
function isoStringToDate(match: RegExpMatchArray, includeTimezone = false): Date {
  const date = new Date(0);
  let tzHour = 0;
  let tzMin = 0;

  // match[8] means that the string contains "Z" (UTC) or a timezone like "+01:00" or "+0100"
  const dateSetter = match[8] && includeTimezone ? date.setUTCFullYear : date.setFullYear;
  const timeSetter = match[8] && includeTimezone ? date.setUTCHours : date.setHours;

  // if there is a timezone defined like "+01:00" or "+0100" and it should be taken into account
  if (match[9] && includeTimezone) {
    tzHour = Number(match[9] + match[10]);
    tzMin = Number(match[9] + match[11]);
  }
  dateSetter.call(date, Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const h = Number(match[4] || 0) - tzHour;
  const m = Number(match[5] || 0) - tzMin;
  const s = Number(match[6] || 0);
  const ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
  timeSetter.call(date, h, m, s, ms);
  return date;
}

/**
 * Converts a value to date.
 *
 * Supported input formats:
 * - `Date`
 * - number: timestamp
 * - string: numeric (e.g. "1234"), ISO and date strings in a format supported by
 *   [Date.parse()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse).
 *   Note: ISO strings without time return a date without timeoffset.
 *
 * Throws if unable to convert to a date.
 *
 * Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 * and altered.
 *
 * Difference to DatePipe:
 * - include/exclude timezone offset
 */
export function toDate(value: DateTransformInput, includeTimezone = false): Date {
  if (isDate(value)) {
    return value;
  }

  if (isNumber(value) && !isNaN(value)) {
    return new Date(value);
  }

  if (isString(value)) {
    value = value.trim();

    const parsedNb = parseFloat(value);

    // any string that only contains numbers, like "1234" but not like "1234hello"
    if (!isNaN((value as any) - parsedNb)) {
      return new Date(parsedNb);
    }

    if (/^(\d{4}-\d{1,2}-\d{1,2})$/.test(value)) {
      /* For ISO Strings without time the day, month and year must be extracted from the ISO String
    before Date creation to avoid time offset and errors in the new Date.
    If we only replace '-' with ',' in the ISO String ("2015,01,01"), and try to create a new
    date, some browsers (e.g. IE 9) will throw an invalid Date error.
    If we leave the '-' ("2015-01-01") and try to create a new Date("2015-01-01") the timeoffset
    is applied.
    Note: ISO months are 0 for January, 1 for February, ... */
      const [y, m, d] = value.split('-').map((val: string) => +val);
      return new Date(y, m - 1, d);
    } else if (/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.test(value)) {
      const dateTime = value.split(' ');
      const [year, month, day] = dateTime[0].split('-').map((val: string) => +val);
      const [hour, minute, second] = dateTime[1].split(':').map((val: string) => +val);
      return new Date(year, month - 1, day, hour, minute, second);
    }

    const match = value.match(ISO8601_DATE_REGEX);
    if (match) {
      return isoStringToDate(match, includeTimezone);
    }
  }

  const date = new Date(value);
  if (!isDate(date)) {
    throw new Error(`Unable to convert "${value}" into a date`);
  }
  return date;
}

// Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
function getDatePart(part: DateType, date: Date): number {
  switch (part) {
    case DateType.FullYear:
      return date.getFullYear();
    case DateType.Month:
      return date.getMonth();
    case DateType.Date:
      return date.getDate();
    case DateType.Hours:
      return date.getHours();
    case DateType.Minutes:
      return date.getMinutes();
    case DateType.Seconds:
      return date.getSeconds();
    case DateType.FractionalSeconds:
      return date.getMilliseconds();
    case DateType.Day:
      return date.getDay();
    default:
      throw new Error(`Unknown DateType value "${part}".`);
  }
}

/**
 * Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 * and altered.
 *
 * Difference to DatePipe:
 * - Removed all non-numeric date-time formats
 */
function getDateFormatter(format: string): DateFormatter | null {
  if (DATE_FORMATS[format]) {
    return DATE_FORMATS[format];
  }
  let formatter;
  switch (format) {
    // 1 digit representation of the year, e.g. (AD 1 => 1, AD 199 => 199)
    case 'y':
      formatter = dateGetter(DateType.FullYear, 1, 0, false, true);
      break;
    // 2 digit representation of the year, padded (00-99). (e.g. AD 2001 => 01, AD 2010 => 10)
    case 'yy':
      formatter = dateGetter(DateType.FullYear, 2, 0, true, true);
      break;
    // 3 digit representation of the year, padded (000-999). (e.g. AD 2001 => 01, AD 2010 => 10)
    case 'yyy':
      formatter = dateGetter(DateType.FullYear, 3, 0, false, true);
      break;
    // 4 digit representation of the year (e.g. AD 1 => 0001, AD 2010 => 2010)
    case 'yyyy':
      formatter = dateGetter(DateType.FullYear, 4, 0, false, true);
      break;

    // Month of the year (1-12), numeric
    case 'M':
    case 'L':
      formatter = dateGetter(DateType.Month, 1, 1);
      break;
    case 'MM':
    case 'LL':
      formatter = dateGetter(DateType.Month, 2, 1);
      break;

    // Week of the year (1, ... 52)
    case 'w':
      formatter = weekGetter(1);
      break;
    case 'ww':
      formatter = weekGetter(2);
      break;

    // Week of the month (1, ...)
    case 'W':
      formatter = weekGetter(1, true);
      break;

    // Day of the month (1-31)
    case 'd':
      formatter = dateGetter(DateType.Date, 1);
      break;
    case 'dd':
      formatter = dateGetter(DateType.Date, 2);
      break;

    // Hour in AM/PM, (1-12)
    case 'h':
      formatter = dateGetter(DateType.Hours, 1, -12);
      break;
    case 'hh':
      formatter = dateGetter(DateType.Hours, 2, -12);
      break;

    // Hour of the day (0-23)
    case 'H':
      formatter = dateGetter(DateType.Hours, 1);
      break;
    // Hour in day, padded (00-23)
    case 'HH':
      formatter = dateGetter(DateType.Hours, 2);
      break;

    // Minute of the hour (0-59)
    case 'm':
      formatter = dateGetter(DateType.Minutes, 1);
      break;
    case 'mm':
      formatter = dateGetter(DateType.Minutes, 2);
      break;

    // Second of the minute (0-59)
    case 's':
      formatter = dateGetter(DateType.Seconds, 1);
      break;
    case 'ss':
      formatter = dateGetter(DateType.Seconds, 2);
      break;

    // Fractional second
    case 'S':
      formatter = dateGetter(DateType.FractionalSeconds, 1);
      break;
    case 'SS':
      formatter = dateGetter(DateType.FractionalSeconds, 2);
      break;
    case 'SSS':
      formatter = dateGetter(DateType.FractionalSeconds, 3);
      break;

    default:
      return null;
  }
  DATE_FORMATS[format] = formatter;
  return formatter;
}

/**
 * Returns a date formatter that transforms a date into its digit representation
 *
 * Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 *
 * Difference to DatePipe:
 * - Changed to be an arrow function
 * - Removed passing the locale.
 */
function dateGetter(name: DateType, size: number, offset = 0, trim = false, negWrap = false): DateFormatter {
  return (date: Date): string => {
    let part = getDatePart(name, date);
    if (offset > 0 || part > -offset) {
      part += offset;
    }

    if (name === DateType.Hours) {
      if (part === 0 && offset === -12) {
        part = 12;
      }
    } else if (name === DateType.FractionalSeconds) {
      return formatFractionalSeconds(part, size);
    }

    // Difference to DatePipe: Set minus sign to hard-coded '-' instead of loading it based on the locale.
    return padNumber(part, size, '-', trim, negWrap);
  };
}

/**
 * Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 *
 * Difference to DatePipe:
 * - Changed to be an arrow function
 * - Removed passing the locale.
 */
function weekGetter(size: number, monthBased = false): DateFormatter {
  return (date: Date): string => {
    let result;
    if (monthBased) {
      const nbDaysBefore1stDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getDay() - 1;
      const today = date.getDate();
      result = 1 + Math.floor((today + nbDaysBefore1stDayOfMonth) / 7);
    } else {
      const firstThurs = getFirstThursdayOfYear(date.getFullYear());
      const thisThurs = getThursdayThisWeek(date);
      const diff = thisThurs.getTime() - firstThurs.getTime();
      result = 1 + Math.round(diff / 6.048e8); // 6.048e8 ms per week
    }

    // Difference to DatePipe: Removed getLocaleNumberSymbol function call for getting the localized minus sign.
    return padNumber(result, size);
  };
}

// Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
function formatFractionalSeconds(milliseconds: number, digits: number): string {
  const strMs = padNumber(milliseconds, 3);
  return strMs.substring(0, digits);
}
